/* eslint-disable max-lines */
import { Input, Spinner } from '@heroui/react';
import { FirebaseError } from 'firebase/app';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';
import { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useActivateAccount, useHeroToast, useSetUserPassword, useVerifyInviteToken } from '@/hooks';
import { useLocaleNavigate, LocaleLink } from '@/utils/locale';

import imageCover from '../../../assets/images/white.png';
import { ReactComponent as Logo } from '../../../assets/logo-hs.svg';
import { auth } from '../../../config';
import { inputProps } from '../../../utils';
import {
  AuthCard,
  BackLink,
  Content,
  CoverSection,
  Form,
  FormSection,
  LoginHeader,
  MainContainer,
  PrimaryButton,
  Title,
} from '../auth.styled';
import { AuthFooterBar } from '../components/AuthFooterBar';

interface SetPasswordFormData {
    newPassword: string;
    confirmPassword: string;
}

const getPasswordRequirements = (t: (key: string) => string) => [
  { label: t('validation.requirements.minLength'), test: (p: string) => p.length >= 6 },
  { label: t('validation.requirements.uppercase'), test: (p: string) => /[A-Z]/.test(p) },
  { label: t('validation.requirements.lowercase'), test: (p: string) => /[a-z]/.test(p) },
  { label: t('validation.requirements.digit'), test: (p: string) => /[0-9]/.test(p) },
  { label: t('validation.requirements.specialChar'), test: (p: string) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

type PageState = 'loading' | 'valid' | 'invalid' | 'expired' | 'success';

export const UserInvite: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useLocaleNavigate();
  const { showError } = useHeroToast();
  const { mutateAsync: activateAccount } = useActivateAccount();
  const { mutateAsync: verifyInviteToken } = useVerifyInviteToken();
  const { mutateAsync: setUserPassword } = useSetUserPassword();

  const getFirebaseErrorMessage = (error: unknown): string => {
    if (error instanceof FirebaseError) {
      const errorKey = `firebaseErrors.${error.code}`;
      const translated = t(errorKey);
      if (translated !== errorKey) {
        return translated;
      }

      const messageKey = `firebaseErrors.${error.message}`;
      const messageTranslated = t(messageKey);
      if (messageTranslated !== messageKey) {
        return messageTranslated;
      }
    }
    return t('firebaseErrors.default');
  };

  const token = searchParams.get('token');

  const [pageState, setPageState] = useState<PageState>('loading');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [userUid, setUserUid] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const { register, handleSubmit, watch } = useForm<SetPasswordFormData>();
  const watchedPassword = watch('newPassword', '');
  const PASSWORD_REQUIREMENTS = getPasswordRequirements(t);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setPageState('invalid');
        return;
      }

      try {
        const result = await verifyInviteToken({ token });
        setUserEmail(result.email || null);
        setUserUid(result.uid || null);
        setPageState('valid');
      } catch (error: any) {
        if (error.message?.includes('expired')) {
          setPageState('expired');
        } else {
          setPageState('invalid');
        }
      }
    };

    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onSubmit = async ({ newPassword, confirmPassword }: SetPasswordFormData) => {
    // Block submission if page state is not valid
    if (pageState !== 'valid') {
      showError(t('firebaseErrors.auth/invalid-action-code'));
      return;
    }

    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    if (newPassword !== confirmPassword) {
      showError(t('firebaseErrors.default'));
      return;
    }

    const allValid = PASSWORD_REQUIREMENTS.every((req) => req.test(newPassword));
    if (!allValid) {
      showError(t('firebaseErrors.auth/weak-password'));
      return;
    }

    if (!userUid || !userEmail) {
      showError('User not found');
      return;
    }

    setIsSubmitting(true);

    try {
      // Set password using backend endpoint (this will also consume the token)
      await setUserPassword({ uid: userUid, newPassword, token: token || undefined });

      // Activate account
      await activateAccount({ email: userEmail });

      // Sign in with new password
      await signInWithEmailAndPassword(auth, userEmail, newPassword);

      setPageState('success');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 3000);
    } catch (error) {
      showError(getFirebaseErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <MainContainer>
        <AuthCard $centered>
          <FormSection>
            <div className="flex flex-col items-center justify-center gap-5 py-8">
              <Spinner size="lg" color="primary" />
              <Content className="text-center mb-0">{t('auth.userInvite.verifyingToken')}</Content>
            </div>
          </FormSection>
        </AuthCard>

        <AuthFooterBar />
      </MainContainer>
    );
  }

  // Invalid or Expired state
  if (pageState === 'invalid' || pageState === 'expired') {
    return (
      <MainContainer>
        <AuthCard $centered>
          <FormSection>
            <LoginHeader>
              <Logo />
            </LoginHeader>
            <Title className="text-center">
              {pageState === 'expired' ? t('auth.userInvite.linkExpired') : t('auth.userInvite.linkInvalid')}
            </Title>
            <div className="p-4 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-700 dark:text-red-400">
                {pageState === 'expired'
                  ? t('auth.userInvite.linkExpiredMessage')
                  : t('auth.userInvite.linkInvalidMessage')}
              </p>
            </div>
            <BackLink>
              <LocaleLink to="/login">{t('auth.userInvite.goToLogin')}</LocaleLink>
            </BackLink>
          </FormSection>
        </AuthCard>

        <AuthFooterBar />
      </MainContainer>
    );
  }

  // Success state
  if (pageState === 'success') {
    return (
      <MainContainer>
        <AuthCard $centered>
          <FormSection>
            <LoginHeader>
              <Logo />
            </LoginHeader>
            <div className="flex flex-col items-center gap-5 py-4">
              <CheckCircle size={64} className="text-success" />
              <Title className="text-center">{t('auth.userInvite.successTitle')}</Title>
              <Content className="text-center">
                {t('auth.userInvite.successMessage')}
              </Content>
              <Spinner size="sm" color="primary" />
            </div>
          </FormSection>
        </AuthCard>

        <AuthFooterBar />
      </MainContainer>
    );
  }

  // Valid state - Form
  return (
    <MainContainer>
      <AuthCard>
        <FormSection>
          <LoginHeader>
            <Logo />
          </LoginHeader>

          <Title>{t('auth.userInvite.title')}</Title>
          <Content>
            {t('auth.userInvite.subtitle1')}
            {' '}
            <strong>{userEmail}</strong>
            {t('auth.userInvite.subtitle2')}
          </Content>

          <Form onSubmit={handleSubmit(onSubmit)}>
            <Input
              {...inputProps}
              type={showPassword ? 'text' : 'password'}
              placeholder={t('auth.form.enterPasswordPlaceholder')}
              endContent={(
                <button type="button" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <Eye strokeWidth={1.5} /> : <EyeOff strokeWidth={1.5} />}
                </button>
                              )}
              {...register('newPassword', { required: true })}
            />

            <div className="p-3 bg-gray-50 dark:bg-[#242a33] rounded-md border border-gray-200 dark:border-[#374151]">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('validation.requirementsLabel')}</p>
              <div className="grid grid-cols-2 gap-1">
                {PASSWORD_REQUIREMENTS.map((req) => {
                  const met = req.test(watchedPassword);
                  return (
                    <div
                      key={req.label}
                      className={`flex items-center gap-1 text-xs transition-colors ${
                        met ? 'text-success' : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {met ? <CheckCircle size={12} /> : <XCircle size={12} />}
                      <span>{req.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <Input
              {...inputProps}
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder={t('auth.form.confirmPasswordPlaceholder')}
              endContent={(
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                  {showConfirmPassword ? <Eye strokeWidth={1.5} /> : <EyeOff strokeWidth={1.5} />}
                </button>
                              )}
              {...register('confirmPassword', { required: true })}
            />

            <PrimaryButton type="submit" disabled={isSubmitting} isLoading={isSubmitting}>
              {!isSubmitting && t('common.submit')}
              {isSubmitting && (
                <>
                  <Spinner size="sm" variant="spinner" classNames={{ circle1: 'border-b-white', circle2: 'border-b-white' }} />
                  <span className="ml-2">{t('common.loading', 'Ładowanie...')}</span>
                </>
              )}
            </PrimaryButton>

            <BackLink>
              <LocaleLink to="/login">{t('auth.userInvite.alreadyHaveAccount')}</LocaleLink>
            </BackLink>
          </Form>
        </FormSection>

        <CoverSection $bg={imageCover} />
      </AuthCard>

      <AuthFooterBar />
    </MainContainer>
  );
};
